<template>
    <div class="p-4">
        <AccionesCabeceraPagina>
            <RouterLink to="/clientes" class="btn btn-sm btn-outline-secondary">
                <i class="bi bi-arrow-left me-sm-1" />
                <span class="d-none d-sm-inline">Volver</span>
            </RouterLink>
        </AccionesCabeceraPagina>

        <!-- Loading -->
        <div v-if="loading" class="text-center py-5">
            <div class="spinner-border spinner-border-sm text-secondary" role="status">
                <span class="visually-hidden">Cargando...</span>
            </div>
        </div>

        <!-- Error -->
        <div v-else-if="error" class="alert alert-danger">{{ error }}</div>

        <!-- Datos -->
        <template v-else-if="cliente">
            <div class="row g-3">
                <!-- Datos fiscales -->
                <div class="col-12 col-lg-6">
                    <div class="card h-100">
                        <div class="card-header py-2">
                            <span class="fw-semibold small">Datos fiscales</span>
                        </div>
                        <div class="card-body">
                            <dl class="row mb-0">
                                <dt class="col-5 text-muted fw-normal small">Nombre fiscal</dt>
                                <dd class="col-7 small">{{ cliente.nombreFiscal ?? '—' }}</dd>

                                <dt class="col-5 text-muted fw-normal small">NIF / CIF</dt>
                                <dd class="col-7 small">{{ cliente.nif ?? '—' }}</dd>
                            </dl>
                        </div>
                    </div>
                </div>

                <!-- Contacto -->
                <div class="col-12 col-lg-6">
                    <div class="card h-100">
                        <div class="card-header py-2">
                            <span class="fw-semibold small">Contacto</span>
                        </div>
                        <div class="card-body">
                            <dl class="row mb-0">
                                <dt class="col-5 text-muted fw-normal small">Teléfono</dt>
                                <dd class="col-7 small">
                                    <a v-if="cliente.telefono" :href="`tel:${cliente.telefono}`">{{ cliente.telefono }}</a>
                                    <span v-else>—</span>
                                </dd>

                                <dt class="col-5 text-muted fw-normal small">Email</dt>
                                <dd class="col-7 small">
                                    <a v-if="cliente.email" :href="`mailto:${cliente.email}`">{{ cliente.email }}</a>
                                    <span v-else>—</span>
                                </dd>
                            </dl>
                        </div>
                    </div>
                </div>

                <!-- Dirección -->
                <div class="col-12 col-lg-6">
                    <div class="card h-100">
                        <div class="card-header py-2">
                            <span class="fw-semibold small">Dirección</span>
                        </div>
                        <div class="card-body">
                            <dl class="row mb-0">
                                <dt class="col-5 text-muted fw-normal small">Dirección</dt>
                                <dd class="col-7 small">{{ cliente.direccion ?? '—' }}</dd>

                                <dt class="col-5 text-muted fw-normal small">Población</dt>
                                <dd class="col-7 small">{{ cliente.poblacion ?? '—' }}</dd>

                                <dt class="col-5 text-muted fw-normal small">C.P.</dt>
                                <dd class="col-7 small">{{ cliente.codigoPostal ?? '—' }}</dd>

                                <dt class="col-5 text-muted fw-normal small">Provincia</dt>
                                <dd class="col-7 small">{{ cliente.provincia?.descripcion ?? '—' }}</dd>
                            </dl>
                        </div>
                    </div>
                </div>

                <!-- Observaciones -->
                <div v-if="cliente.observaciones1" class="col-12 col-lg-6">
                    <div class="card h-100">
                        <div class="card-header py-2">
                            <span class="fw-semibold small">Observaciones</span>
                        </div>
                        <div class="card-body">
                            <p class="small mb-0" style="white-space: pre-wrap;">{{ cliente.observaciones1 }}</p>
                        </div>
                    </div>
                </div>

                <!-- Avisos abiertos -->
                <div class="col-12">
                    <div class="card">
                        <div class="card-header py-2">
                            <span class="fw-semibold small">Avisos abiertos</span>
                        </div>
                        <div class="card-body p-0">
                            <div v-if="loadingAvisos" class="text-center py-3">
                                <div class="spinner-border spinner-border-sm text-secondary" role="status" />
                            </div>
                            <div v-else-if="avisos.length === 0" class="text-center text-muted py-3 small">Sin avisos abiertos.</div>
                            <template v-else>
                                <!-- Cards (mobile) -->
                                <div class="d-md-none overflow-hidden">
                                    <RouterLink
                                        v-for="aviso in avisos"
                                        :key="aviso.idAviso"
                                        :to="{ name: 'aviso-detail', params: { idAviso: aviso.idAviso } }"
                                        class="border-bottom px-3 py-2 text-decoration-none d-block text-dark"
                                    >
                                        <div class="d-flex justify-content-between align-items-center mb-1">
                                            <span class="text-muted small">#{{ aviso.idAviso }} · {{ formatFecha(aviso.fecha) }}</span>
                                        </div>
                                        <div class="small text-clamp-2">{{ aviso.descripcion }}</div>
                                        <div v-if="aviso.asignacionesTexto" class="small text-muted mt-1">{{ aviso.asignacionesTexto }}</div>
                                    </RouterLink>
                                </div>

                                <!-- Tabla (desktop) -->
                                <div class="d-none d-md-block table-responsive">
                                    <table class="table table-hover table-sm mb-0">
                                        <thead class="table-light">
                                            <tr>
                                                <th class="ps-3">#</th>
                                                <th>Fecha</th>
                                                <th>Descripción</th>
                                                <th class="d-none d-lg-table-cell">Asignaciones</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr
                                                v-for="aviso in avisos"
                                                :key="aviso.idAviso"
                                                class="row-link"
                                            >
                                                <td class="ps-3 text-muted small">
                                                    <RouterLink :to="{ name: 'aviso-detail', params: { idAviso: aviso.idAviso } }" class="text-decoration-none text-muted d-block">
                                                        {{ aviso.idAviso }}
                                                    </RouterLink>
                                                </td>
                                                <td class="small text-nowrap">
                                                    <RouterLink :to="{ name: 'aviso-detail', params: { idAviso: aviso.idAviso } }" class="text-decoration-none text-dark d-block">
                                                        {{ formatFecha(aviso.fecha) }}
                                                    </RouterLink>
                                                </td>
                                                <td class="small">
                                                    <RouterLink :to="{ name: 'aviso-detail', params: { idAviso: aviso.idAviso } }" class="text-decoration-none text-dark d-block">
                                                        {{ aviso.descripcion }}
                                                    </RouterLink>
                                                </td>
                                                <td class="small d-none d-lg-table-cell text-muted">
                                                    <RouterLink :to="{ name: 'aviso-detail', params: { idAviso: aviso.idAviso } }" class="text-decoration-none text-muted d-block">
                                                        {{ aviso.asignacionesTexto || '—' }}
                                                    </RouterLink>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </template>
                        </div>
                    </div>
                </div>

                <!-- Últimos partes -->
                <div class="col-12">
                    <div class="card">
                        <div class="card-header py-2">
                            <RouterLink :to="{ name: 'mis-partes', query: { idc, todos: '1' } }" class="fw-semibold small text-decoration-none text-body">
                                Últimos partes <i class="bi bi-arrow-right ms-1 small" />
                            </RouterLink>
                        </div>
                        <div class="card-body p-0">
                            <div v-if="loadingPartes" class="text-center py-3">
                                <div class="spinner-border spinner-border-sm text-secondary" role="status" />
                            </div>
                            <div v-else-if="partes.length === 0" class="text-center text-muted py-3 small">Sin partes.</div>
                            <template v-else>
                                <!-- Cards (mobile) -->
                                <div class="d-md-none overflow-hidden">
                                    <RouterLink
                                        v-for="parte in partes"
                                        :key="parte.idParte"
                                        :to="{ name: 'parte-detail', params: { idParte: parte.idParte } }"
                                        class="border-bottom px-3 py-2 text-decoration-none d-block text-dark"
                                    >
                                        <div class="d-flex justify-content-between align-items-center mb-1">
                                            <span class="text-muted small">#{{ parte.idParte }} · {{ formatFecha(parte.fecha) }}</span>
                                            <span class="badge" :class="parte.cerrado ? 'bg-success' : 'bg-warning text-dark'">
                                                {{ parte.cerrado ? 'Cerrado' : 'Abierto' }}
                                            </span>
                                        </div>
                                        <div class="small">{{ parte.tipoParte?.descripcion ?? '—' }} <span v-if="parte.usuario?.nombre" class="text-muted">· {{ parte.usuario.nombre }}</span></div>
                                        <div v-if="parte.observaciones" class="small text-muted text-clamp-2">{{ parte.observaciones }}</div>
                                        <div v-if="parte.tramosString" class="small text-muted mt-1" v-html="parte.tramosString" />
                                    </RouterLink>
                                </div>

                                <!-- Tabla (desktop) -->
                                <div class="d-none d-md-block table-responsive">
                                    <table class="table table-hover table-sm mb-0">
                                        <thead class="table-light">
                                            <tr>
                                                <th class="ps-3">#</th>
                                                <th>Fecha</th>
                                                <th>Tipo</th>
                                                <th class="d-none d-lg-table-cell">Usuario</th>
                                                <th class="d-none d-lg-table-cell">Observaciones</th>
                                                <th>Tramos</th>
                                                <th>Estado</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr
                                                v-for="parte in partes"
                                                :key="parte.idParte"
                                                class="row-link"
                                            >
                                                <td class="ps-3 text-muted small">
                                                    <RouterLink :to="{ name: 'parte-detail', params: { idParte: parte.idParte } }" class="text-decoration-none text-muted d-block">
                                                        {{ parte.idParte }}
                                                    </RouterLink>
                                                </td>
                                                <td class="small text-nowrap">
                                                    <RouterLink :to="{ name: 'parte-detail', params: { idParte: parte.idParte } }" class="text-decoration-none text-dark d-block">
                                                        {{ formatFecha(parte.fecha) }}
                                                    </RouterLink>
                                                </td>
                                                <td class="small">
                                                    <RouterLink :to="{ name: 'parte-detail', params: { idParte: parte.idParte } }" class="text-decoration-none text-dark d-block">
                                                        {{ parte.tipoParte?.descripcion ?? '—' }}
                                                    </RouterLink>
                                                </td>
                                                <td class="small d-none d-lg-table-cell text-muted">
                                                    <RouterLink :to="{ name: 'parte-detail', params: { idParte: parte.idParte } }" class="text-decoration-none text-muted d-block">
                                                        {{ parte.usuario?.nombre ?? '—' }}
                                                    </RouterLink>
                                                </td>
                                                <td class="small d-none d-lg-table-cell" style="max-width: 220px;">
                                                    <RouterLink :to="{ name: 'parte-detail', params: { idParte: parte.idParte } }" class="text-decoration-none text-dark d-block">
                                                        <div v-if="parte.observaciones" class="text-clamp-2" :title="parte.observaciones">{{ parte.observaciones }}</div>
                                                        <span v-else class="text-muted">—</span>
                                                    </RouterLink>
                                                </td>
                                                <td class="small" style="white-space: nowrap;">
                                                    <RouterLink :to="{ name: 'parte-detail', params: { idParte: parte.idParte } }" class="text-decoration-none text-dark d-block" v-html="parte.tramosString || '—'" />
                                                </td>
                                                <td>
                                                    <RouterLink :to="{ name: 'parte-detail', params: { idParte: parte.idParte } }" class="text-decoration-none d-block">
                                                        <span class="badge" :class="parte.cerrado ? 'bg-success' : 'bg-warning text-dark'">
                                                            {{ parte.cerrado ? 'Cerrado' : 'Abierto' }}
                                                        </span>
                                                    </RouterLink>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </template>
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { clientesApi } from '@/api/clientesApi'
import { avisosApi } from '@/api/avisosApi'
import { partesApi } from '@/api/partesApi'
import AccionesCabeceraPagina from '@/components/AccionesCabeceraPagina.vue'
import type { Cliente } from '@/types/cliente'
import type { Aviso } from '@/types/aviso'
import type { Parte } from '@/types/parte'
import { usePageHeader } from '@/composables/usePageHeader'
import { useBreadcrumbs } from '@/composables/useBreadcrumbs'

const route = useRoute()
const router = useRouter()
const idc = route.params.idc as string
const { title, mobileTitle } = usePageHeader(idc, idc)
const { crumbs } = useBreadcrumbs([{ label: 'Clientes', to: '/clientes' }, { label: idc }])

const loading = ref(false)
const error = ref<string | null>(null)
const cliente = ref<Cliente | null>(null)
const avisos = ref<Aviso[]>([])
const loadingAvisos = ref(false)
const partes = ref<Parte[]>([])
const loadingPartes = ref(false)

function formatFecha(fecha: string): string {
    return new Date(fecha).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

onMounted(async () => {
    loading.value = true
    error.value = null
    try {
        cliente.value = await clientesApi.getById(idc)
        const nombre = cliente.value?.nombreFiscal ?? idc
        title.value = nombre
        mobileTitle.value = idc
        crumbs.value[1].label = nombre
    } catch {
        error.value = 'No se pudo cargar el cliente.'
    } finally {
        loading.value = false
    }

    loadingPartes.value = true
    try {
        const result = await partesApi.getAll({ idCliente: idc, pageSize: 10 })
        partes.value = result.items
    } catch {
        // no bloqueante
    } finally {
        loadingPartes.value = false
    }

    loadingAvisos.value = true
    try {
        const result = await avisosApi.getAll({ idCliente: idc, finalizado: '0', pageSize: 100 })
        avisos.value = result.items
    } catch {
        // no bloqueante
    } finally {
        loadingAvisos.value = false
    }
})
</script>

<style scoped>
.text-clamp-2 {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}
</style>
